import React, { useState, useEffect } from 'react';
import theme from '../theme';

const Home = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    // Hero Section
    hero: {
      padding: `${theme.spacing['3xl']} ${theme.spacing.md}`,
      backgroundColor: theme.colors.background,
      textAlign: 'center',
    },
    heroContainer: {
      maxWidth: '1280px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing.xl,
      alignItems: 'center',
    },
    heroContent: {
      textAlign: isMobile ? 'center' : 'left',
    },
    heroTitle: {
      fontSize: isMobile ? theme.typography.fontSize['3xl'] : theme.typography.fontSize['7xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      lineHeight: theme.typography.lineHeight.tight,
      fontFamily: theme.typography.fontFamily.heading,
    },
    heroDescription: {
      fontSize: isMobile ? theme.typography.fontSize.base : theme.typography.fontSize.xl,
      color: theme.colors.textSecondary,
      marginBottom: theme.spacing.xl,
      lineHeight: theme.typography.lineHeight.relaxed,
      fontFamily: theme.typography.fontFamily.primary,
    },
    ctaButtons: {
      display: 'flex',
      gap: theme.spacing.md,
      flexWrap: 'wrap',
      marginBottom: theme.spacing.xl,
      justifyContent: isMobile ? 'center' : 'flex-start',
      flexDirection: 'column' ,
    },
    ctaButton: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      borderRadius: theme.borderRadius.md,
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: theme.transitions.base,
      fontFamily: theme.typography.fontFamily.primary,
      border: 'none',
      cursor: 'pointer',
    },
    ctaPrimary: {
      backgroundColor: theme.colors.primary,
      color: theme.colors.white,
    },
    ctaSecondary: {
      backgroundColor: theme.colors.white,
      color: theme.colors.primary,
      border: `2px solid ${theme.colors.primary}`,
    },
    wireframeImage: {
      width: '100%',
      maxWidth: '500px',
      height: '400px',
      backgroundColor: theme.colors.border,
      borderRadius: theme.borderRadius.lg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSize['2xl'],
      fontWeight: theme.typography.fontWeight.medium,
      fontFamily: theme.typography.fontFamily.primary,
    },

    // Features Section
    section: {
      padding: `${theme.spacing['3xl']} ${theme.spacing.md}`,
      backgroundColor: theme.colors.background,
    },
    sectionTitle: {
      fontSize: theme.typography.fontSize['4xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.heading,
    },
    sectionSubtitle: {
      fontSize: theme.typography.fontSize.xl,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: theme.spacing['2xl'],
      fontFamily: theme.typography.fontFamily.heading,
    },
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: theme.spacing.xl,
    },
    featureCard: {
      padding: theme.spacing.lg,
      backgroundColor: theme.colors.backgroundGray,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.md,
      transition: theme.transitions.base,
    },
    featureIcon: {
      fontSize: theme.typography.fontSize['4xl'],
      marginBottom: theme.spacing.md,
    },
    featureTitle: {
      fontSize: theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      fontFamily: theme.typography.fontFamily.heading,
    },
    featureDescription: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.textSecondary,
      lineHeight: theme.typography.lineHeight.relaxed,
      fontFamily: theme.typography.fontFamily.primary,
    },

    // Stats Section
    statsSection: {
      padding: `${theme.spacing['3xl']} ${theme.spacing.md}`,
      backgroundColor: theme.colors.primary,
      color: theme.colors.white,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing.xl,
      maxWidth: '1280px',
      margin: '0 auto',
      textAlign: 'center',
    },
    statCard: {
      padding: theme.spacing.lg,
    },
    statNumber: {
      fontSize: theme.typography.fontSize['5xl'],
      fontWeight: theme.typography.fontWeight.extrabold,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.heading,
    },
    statLabel: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.medium,
      opacity: 0.9,
      fontFamily: theme.typography.fontFamily.heading,
    },
  };

const features = [
    {
      icon: '🎙️',
      title: 'Voice Logging',
      description: 'Log workouts hands-free with WODGoat AI, powered by Gemini for speech transcription and exercise structuring.'
    },
    {
      icon: '⏱️',
      title: 'Workout Timer',
      description: 'Keep track of your workout time with a built-in timer, perfect for HIIT and circuit training.'
    },
    {
      icon: '📈',
      title: 'Progress Tracking',
      description: 'Monitor your personal records and track your progress over time with detailed analytics.'
    },
    {
      icon: '🏆',
      title: 'Achievements & PR Cards',
      description: 'Celebrate milestones with shareable personal record cards and unlockable achievements.'
    }
  ];

  // const stats = [
  //   { number: '50K+', label: 'Active Users' },
  //   { number: '1M+', label: 'Workouts Completed' },
  //   { number: '4.8', label: 'App Rating' },
  //   { number: '100+', label: 'Exercise Variations' }
  // ];

  return (
    <div>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContainer}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: theme.spacing.xl }}> 
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: isMobile ? 'center' : 'left' }}>
              <h1 style={styles.heroTitle}>
                One Step Closer to Becoming the G.O.A.T🐐
              </h1>
              <p style={styles.heroDescription}>
                Log workouts hands-free with WODGoat AI — Voice-powered tracking, automatic exercise structuring, workout timer, personal records, and shareable achievements.
              </p>
              <div style={{ ...styles.ctaButtons }}>
                <a 
                  href="https://apps.apple.com/us/app/wodgoat/id6761220753"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...styles.ctaButton, ...styles.ctaPrimary }}
                  onMouseOver={(e) => e.target.style.backgroundColor = theme.colors.primaryDark}
                  onMouseOut={(e) => e.target.style.backgroundColor = theme.colors.primary}
                >
                  📱 Download on App Store
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.wodgoat&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...styles.ctaButton, ...styles.ctaSecondary }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = theme.colors.primary;
                    e.target.style.color = theme.colors.white;
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = theme.colors.white;
                    e.target.style.color = theme.colors.primary;
                  }}
                >
                  🤖 Get it on Google Play
                </a>
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center',padding: isMobile? theme.spacing.lg: 0 }}>
              <img 
                src={require('../assets/images/mockup.png')} 
                alt="App Preview" 
                style={{ width: '100%', maxWidth: '800px', height: 'auto', objectFit: 'cover', borderRadius: theme.borderRadius.lg }} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={styles.section}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Powerful Features</h2>
          <p style={styles.sectionSubtitle}>
            Everything you need to reach your fitness goals
          </p>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div 
                key={index} 
                style={styles.featureCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = theme.shadows.xl;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.shadows.md;
                }}
              >
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section
      <section style={styles.statsSection}>
        <div style={styles.container}>
          <h2 style={{...styles.sectionTitle, color: theme.colors.white, marginBottom: theme.spacing['2xl']}}>
            Trusted by Thousands
          </h2>
          <div style={styles.statsGrid}>
            {stats.map((stat, index) => (
              <div key={index} style={styles.statCard}>
                <div style={styles.statNumber}>{stat.number}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Home;
